import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Filter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { 
  getListings, 
  getCategories, 
  addToFavorites, 
  removeFromFavorites, 
  checkIsFavorite 
} from '../../services/api';
import Header from '../../components/layout/Header';
import DesktopSidebar from '../../components/layout/DesktopSidebar';
import MobileCategories from '../../components/layout/MobileCategories';
import MobileFilterModal from '../../components/layout/MobileFilterModal';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Footer from '../../components/layout/Footer';
import HeroCarousel from './HeroCarousel';
import MobileHero from './MobileHero';
import RecommendedForYou from './RecommendedForYou';
import TodayDeals from './TodayDeals';
import ProductGrid from './ProductGrid';
import { GridLoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { AlertModal } from '../../components/common/Modal';

export default function Home() {
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: null,
    minPrice: null,
    maxPrice: null,
    condition: 'all',
    location: null,
    sortBy: 'newest',
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { user } = useAuth();
  
  // Optimized favorite states
  const [favoritesMap, setFavoritesMap] = useState({});
  const [loadingFavorites, setLoadingFavorites] = useState({});
  const [modal, setModal] = useState({ show: false, message: '', type: 'info' });
  
  const productGridRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate filtered listings
const filteredListings = useMemo(() => {
  return listings
    .filter(listing => {
      // Filter out user's own listings
      if (user && listing.seller_id === user.user_id) return false;
      
      if (filters.category && listing.category_id !== filters.category) return false;
      if (filters.condition !== 'all' && listing.condition !== filters.condition) return false;
      if (filters.minPrice && parseFloat(listing.price) < filters.minPrice) return false;
      if (filters.maxPrice && parseFloat(listing.price) > filters.maxPrice) return false;
      if (filters.location && listing.state_id !== filters.location) return false;
      return true;
    })
    .map(listing => {
      const listingId = listing.listing_id;
      const isLoading = loadingFavorites[listingId] || false;
      
      return {
        ...listing,
        isFavorite: favoritesMap[listingId] || false,
        isLoading: loadingFavorites[listingId] || false,
        onFavoriteClick: (listingId, shouldBeFavorite) => 
          handleFavoriteToggle(listingId, shouldBeFavorite, listing.title)
      };
    });
}, [listings, filters, favoritesMap, loadingFavorites, user]); // Added user to dependencies

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [listingsData, categoriesData] = await Promise.all([
        getListings({ status: 'active', moderation_status: 'approved' }),
        getCategories()
      ]);
  
      setListings(listingsData.data || listingsData || []);
      setCategories(categoriesData.data || categoriesData || []);
      
    } catch (error) {
      // console.error('Error fetching data:', error);
      setListings([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };


  // Optimized: Check favorite status only when clicked
  // Optimized: Check favorite status only when clicked
// Simple and effective: Just toggle and handle errors
const handleFavoriteToggle = useCallback(async (listingId, shouldBeFavorite, listingTitle = 'Item') => {
  if (!user) {
    setModal({ show: true, message: 'Please login to add items to favorites', type: 'warning' });
    return;
  }

  setLoadingFavorites(prev => ({ ...prev, [listingId]: true }));

  const currentStatus = favoritesMap[listingId];
  
  try {
    // OPTIMISTIC UPDATE
    setFavoritesMap(prev => ({ ...prev, [listingId]: shouldBeFavorite }));
    
    if (shouldBeFavorite) {
      const response = await addToFavorites(user.user_id, listingId);
      
      // CASE-INSENSITIVE CHECK
      const errorMessage = response.message?.toLowerCase() || '';
      if (!response.success && errorMessage.includes('already')) {
        // Already in favorites - show positive message
        setModal({
          show: true,
          message: `"${listingTitle}" is already in your favorites!`,
          type: 'info'
        });
        setFavoritesMap(prev => ({ ...prev, [listingId]: true }));
        return;
      }
      
      if (!response.success) {
        setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || false }));
        setModal({
          show: true,
          message: response.message || 'Failed to add to favorites',
          type: 'error'
        });
        return;
      }
      
      // Success
      setModal({
        show: true,
        message: `"${listingTitle}" added to favorites!`,
        type: 'success'
      });
      
    } else {
      const response = await removeFromFavorites(user.user_id, listingId);
      
      if (!response.success) {
        setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || true }));
        setModal({
          show: true,
          message: response.message || 'Failed to remove from favorites',
          type: 'error'
        });
        return;
      }
      
      // Success
      setModal({
        show: true,
        message: `"${listingTitle}" removed from favorites`,
        type: 'info'
      });
    }
  } catch (error) {
    // console.error('Error toggling favorite:', error);
    setFavoritesMap(prev => ({ ...prev, [listingId]: currentStatus || false }));
    setModal({
      show: true,
      message: 'Network error. Please check your connection.',
      type: 'error'
    });
  } finally {
    setLoadingFavorites(prev => ({ ...prev, [listingId]: false }));
  }
}, [user, favoritesMap]);


  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    
    setTimeout(() => {
      if (productGridRef.current) {
        productGridRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleClearFilters = () => {
    setFilters({
      category: null,
      minPrice: null,
      maxPrice: null,
      condition: 'all',
      location: null,
      sortBy: 'newest',
    });
  };

  const handleSortChange = (value) => {
    setFilters(prev => ({ ...prev, sortBy: value }));
  };

  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
  };

  const handleCategoryPress = (category) => {
    setFilters(prev => ({
      ...prev,
      category: category?.id || null
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <Header />
        <GridLoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />

      {/* Success Modal */}
      <AlertModal
        isOpen={modal.show}
        onClose={() => setModal(prev => ({ ...prev, show: false }))}
        title={
          modal.type === 'success' ? 'Success!' : 
          modal.type === 'warning' ? 'Notice' : 
          modal.type === 'error' ? 'Error' : 'Info'
        }
        message={modal.message}
        type={modal.type}
        buttonText="OK"
        onButtonClick={() => setModal(prev => ({ ...prev, show: false }))}
      />

      {/* Mobile Categories */}
      <div className="lg:hidden">
        <div className="max-w-7xl mx-auto px-4">
          <MobileCategories 
            categories={categories}
            listings={listings}
            user={user}
            onCategoryPress={handleCategoryPress}
          />
        </div>
      </div>

      {/* Main Content Area - FIXED LAYOUT */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar - FULL SCREEN HEIGHT */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 h-[calc(100vh-96px)]">
              <DesktopSidebar 
                categories={categories}
                filters={filters}
                onFilterChange={handleFilterChange}
                listings={listings}
              />
            </div>
          </div>

          {/* Main Content - Takes remaining space */}
          <div className="flex-1 min-w-0">
            {/* Mobile Hero */}
            <div className="mb-6 lg:hidden">
              <HeroCarousel />
            </div>

            {/* Hero Carousel - Desktop */}
            <div className="mb-6 hidden lg:block">
              <HeroCarousel />
            </div>

            {/* Recommended For You */}
            <div className="mb-6">
              <RecommendedForYou 
                user={user}
                listings={listings}
                userWishlist={[]}
              />
            </div>

            {/* Today's Deals */}
            <div className="mb-6">
            <TodayDeals listings={listings} currentUser={user} />

            </div>

            {/* Product Grid Header */}
            <div 
              ref={productGridRef} 
              className="mb-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">
                    All Products
                  </h2>
                  <p className="text-sm text-gray-600 hidden md:block">
                    Discover amazing products from sellers across Nigeria
                    {user && (
                      <span className="ml-3 inline-flex items-center text-pink-600">
                        <span className="w-2 h-2 bg-pink-500 rounded-full mr-1"></span>
                        {Object.values(favoritesMap).filter(Boolean).length} items in your favorites
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Filter Button (Mobile) */}
                  <button 
                    onClick={toggleMobileFilters}
                    className="lg:hidden flex items-center px-4 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55a0d] font-medium text-sm"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filter
                  </button>

                  {/* Favorites Info */}
                  {user && (
                    <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                      <span>
                        {Object.values(favoritesMap).filter(Boolean).length} items in favorites
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <div>
              <ProductGrid 
                listings={filteredListings}
                viewMode="grid"
                onFavoriteClick={handleFavoriteToggle}
              />
            </div>

            {/* Empty State */}
            {filteredListings.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-3xl">🔍</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No products found
                </h3>
                <p className="text-gray-600 max-w-sm mx-auto mb-6 text-sm">
                  Try adjusting your filters or check back later for new listings.
                </p>
                <button
                  onClick={handleClearFilters}
                  className="px-5 py-2.5 bg-[#ff660e] text-white rounded-lg hover:bg-[#e55a0d] font-medium text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            )}

            {/* Load More Button */}
            {filteredListings.length > 12 && (
              <div className="text-center mt-8">
                <button 
                  onClick={() => {/* Implement load more logic */}}
                  className="px-6 py-2.5 border-2 border-[#ff660e] text-[#ff660e] rounded-lg hover:bg-[#fff5e6] font-medium text-sm"
                >
                  Load More Products
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Mobile Filter Modal */}
      <MobileFilterModal
        isOpen={showMobileFilters}
        onClose={toggleMobileFilters}
        filters={filters}
        onFilterChange={handleFilterChange}
        listingCount={filteredListings.length}
      />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}